import { useFormik } from 'formik'
import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import * as Yup from 'yup'
import { axiosClientAuth } from '../../../app/utils/axiosClient'
import { KTIcon } from '../../helpers'
import Swal from 'sweetalert2'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'
import { RESOURCE_ANALYTICS_CACHE_KEY } from '../../../app/hooks/useResourceAnalytics'
import { useIntl } from 'react-intl'

type Props = {
  show: boolean
  handleClose: () => void
}

const initialValues = {
  file: '',
}

const IPBulkUploadModal = ({ show, handleClose }: Props) => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const queryClient = useQueryClient()

  // Centralised error handler — works for both non-2xx (catch) and status:false (try)
  const handleBulkUploadError = (responseData: any) => {
    const fileError = responseData?.data?.file
    const duplicates: string[] = Array.isArray(fileError?.duplicates) ? fileError.duplicates : []

    // Always use data.file.message if present, then top-level message, then fallback
    const message =
      fileError?.message ||
      responseData?.message ||
      intl.formatMessage({ id: 'MYIP.MESSAGES.BULK_IP_CREATED' })

    const gridHtml =
      duplicates.length > 0
        ? `<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:6px 12px;max-height:200px;overflow-y:auto;text-align:left;margin-top:12px;padding:0 4px;">
            ${duplicates.map((d, i) => `<div style="font-size:13px;color:#555;"><span style="font-weight:600;color:#333;">${i + 1}.</span> ${d}</div>`).join('')}
          </div>`
        : ''

    Swal.fire({
      titleText: intl.formatMessage({ id: 'COMMON.ALERTS.UPLOAD_FAILED' }),
      html: `<p style="margin-bottom:${duplicates.length > 0 ? '4px' : '0'}">${message}</p>${gridHtml}`,
      icon: 'error',
      confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK_GOT_IT' }),
      width: duplicates.length > 0 ? 560 : undefined,
    })
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        if (!values.file) {
          Swal.fire({
            titleText: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
            text: intl.formatMessage({ id: 'MYIP.MESSAGES.UPLOAD_FILE_REQUIRED' }),
            icon: 'error',
            confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK_GOT_IT' }),
          })
          setLoading(false)
          return
        }
        const res = await axiosClientAuth.post('/ip/bulk-upload/', {
          file: values.file,
        }, { headers: { 'Content-Type': 'multipart/form-data' } })

        if (res.data.status) {
          // Success
          Swal.fire({
            titleText: intl.formatMessage({ id: 'COMMON.ALERTS.SUCCESS' }),
            text: intl.formatMessage({ id: 'MYIP.MESSAGES.BULK_IP_CREATED' }),
            icon: 'success',
            confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK_GOT_IT' }),
          }).then(() => {
            queryClient.invalidateQueries('ip-list')
            queryClient.invalidateQueries(RESOURCE_ANALYTICS_CACHE_KEY)
            closeModal()
          })
        } else {
          // API returned 200 but status:false — parse the BE error the same way
          handleBulkUploadError(res.data)
        }
        setLoading(false)
      } catch (error: any) {
        // Non-2xx HTTP error — parse the same way
        handleBulkUploadError(error?.response?.data)
        setLoading(false)
      }
    },
  })

  const closeModal = () => {
    formik.resetForm()
    handleClose()
  }

  return (
    <Modal
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered'
      show={show}
      onHide={handleClose}
    >
      <div className='modal-body py-lg-10 px-lg-10'>
        <div className='d-flex justify-content-end'>
          <button
            className='ms-auto btn btn-sm btn-icon btn-active-color-primary'
            onClick={closeModal}
          >
            <KTIcon iconName='cross' className='text-gray fs-1' />
          </button>
        </div>

        <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
          <div className='mb-13 text-center'>
            <h1 className='mb-3'>{intl.formatMessage({ id: 'MYIP.MODAL.BULK_ADD_TITLE' })}</h1>
            <div className='text-muted fw-semibold fs-5'>
              {intl.formatMessage({ id: 'MYIP.MODAL.DOWNLOAD_SAMPLE_LABEL' })}{' '}
              <a href={window.location.origin + "/media/samples/Sample Format (Don't Change the header).csv"} className='fw-bold link-primary'>
                {intl.formatMessage({ id: 'COMMON.BUTTON.CLICK_HERE' })}
              </a>
              .
            </div>
          </div>

          {formik.status ? (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          ) : null}

          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark required'>
              {intl.formatMessage({ id: 'MYIP.FORM.UPLOAD_FILE_LABEL' })}
            </label>
            <input
              placeholder={intl.formatMessage({ id: 'MYIP.FORM.IP_NAME_PLACEHOLDER' })}
              {...formik.getFieldProps('name')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.file && formik.errors.file },
                {
                  'is-valid': formik.touched.file && !formik.errors.file,
                }
              )}
              type='file'
              name='file'
              autoComplete='off'
              onChange={(event: any) => {
                formik.setFieldValue("file", event?.currentTarget?.files[0]);
              }}
            />
            {formik.touched.file && formik.errors.file && (
              <div className='fv-plugins-message-container'>
                <span role='alert'>{formik.errors.file}</span>
              </div>
            )}
          </div>

          {/* begin::Action */}
          <div className='text-center mb-10'>
            <button type='reset' className='btn btn-light me-3' onClick={closeModal}>
              <span className='indicator-label'>{intl.formatMessage({ id: 'COMMON.BUTTON.CANCEL' })}</span>
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!loading && <span className='indicator-label'>{intl.formatMessage({ id: 'COMMON.BUTTON.SUBMIT' })}</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'COMMON.MESSAGES.PLEASE_WAIT' })}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default IPBulkUploadModal
